import { getRailsClient } from "sj-ui-toolkit/src/client";
import { downloadCsv } from "sj-ui-toolkit/src/utils/downloadFile";

export interface ReportParams {
  from?: string | number;
  to?: string | number;
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// Customer transactions
// ---------------------------------------------------------------------------

interface CustomerTransactionEntity {
  userCount?: number | null;
  expiration?: string | null;
}

export interface CustomerTransaction {
  transactionType?: string;
  description?: string;
  amount?: number;
  createdAt?: string;
  currentBalance?: number;
  billingType?: string;
  entity?: CustomerTransactionEntity | null;
  /** Formatted date string added client-side for DataTable CSV export */
  formattedDate?: string;
}

export interface CustomerTransactionsResponse {
  customerTransactions?: CustomerTransaction[];
  totalPages?: number;
}

interface CustomerTransactionParams {
  page?: number;
  per_page?: number;
  query?: string | null;
  filters?: string[];
}

export const loadCustomerTransactions = async (
  params: CustomerTransactionParams = {}
): Promise<CustomerTransactionsResponse> => {
  const API = "/api/internal_api/v1/admin/reports/customer_transaction";
  const { data } = await getRailsClient().get<CustomerTransactionsResponse>(API, { params });
  return data || {};
};

export const exportUsageReportCSV = async (params: ReportParams = {}): Promise<void> => {
  const API = "/api/internal_api/v1/admin/reports/usage.csv";
  const { data } = await getRailsClient().get<string>(API, { params });
  downloadCsv(data, `usage_report_${new Date().toISOString().split("T")[0]}.csv`);
};

export interface LessonLevelData {
  afterRating?: number;
  afterCount?: number;
  beforeRating?: number;
  beforeCount?: number;
  percentIncrease?: number;
}

export interface DojoLevelData {
  inProgress?: { count?: number; points?: number };
  launched?: { count?: number; points?: number };
  passed?: { count?: number; points?: number };
}

export interface LevelsVPResponse {
  lessonLevels?: Record<string, LessonLevelData>;
  dojoLevels?: Record<string, DojoLevelData>;
}

export interface CourseData {
  id?: string;
  name?: string;
  rankName?: string;
  [key: string]: unknown;
}

export interface CurrentLicense {
  userCount?: number;
  currentUserCount?: number;
  currentAdminsCount?: number;
  currentArchivedCount?: number;
  currentUnlicensedCount?: number;
  expiration?: string;
}

export interface UsageResponse {
  levels?: { name: string | null; consumed: number | null }[];
  totalConsumed?: number;
  accountBalance?: number;
  lessonPrice?: number;
  dojoActivityPrice?: number;
  awarenessLessonPrice?: number;
  experimentPrice?: number;
  currentLicense?: CurrentLicense;
  [key: string]: unknown;
}

/**
 * Load usage report data
 * @param {Object} params - Query parameters (from, to dates)
 * @returns {Promise<Object>} Usage report data
 */
export const loadUsageReport = async (params: ReportParams = {}): Promise<UsageResponse> => {
  const API = "/api/internal_api/v1/admin/reports/usage";
  const { data } = await getRailsClient().get<UsageResponse>(API, { params });
  return data || {};
};

/**
 * Load value proposition levels data (aggregated rankings stats)
 * @param {Object} params - Query parameters (from, to dates)
 * @returns {Promise<Object>} Levels VP data with lessonLevels and dojoLevels
 */
export const loadLevelsVP = async (params: ReportParams = {}): Promise<LevelsVPResponse> => {
  const API = "/api/internal_api/v1/admin/value_proposition/levels";
  const { data } = await getRailsClient().get<LevelsVPResponse>(API, {
    params: {
      from: params.from,
      to: params.to,
    },
  });
  return data || {};
};

export interface LessonData {
  name?: string | null;
  beforeCount?: number;
  beforeRating?: number;
  afterCount?: number;
  afterRating?: number;
  percentIncrease?: number;
}

export interface LessonsVPResponse {
  lessons?: LessonData[];
}

export const loadLessonsVP = async (params: ReportParams = {}): Promise<LessonsVPResponse> => {
  const API = "/api/internal_api/v1/admin/value_proposition/lessons";
  const { data } = await getRailsClient().get<LessonsVPResponse>(API, {
    params: {
      from: params.from,
      to: params.to,
    },
  });
  return data || {};
};

export const exportRankingsCSV = async (params: ReportParams = {}): Promise<void> => {
  const csvParams = { file_format: "csv", from: params.from, to: params.to };

  const levelsAPI = "/api/internal_api/v1/admin/value_proposition/levels.csv";
  const levelsResponse = await getRailsClient().get<string>(levelsAPI, { params: csvParams });
  downloadCsv(levelsResponse.data, "learning_swing_levels.csv");

  const lessonsAPI = "/api/internal_api/v1/admin/value_proposition/lessons.csv";
  const lessonsResponse = await getRailsClient().get<string>(lessonsAPI, { params: csvParams });
  downloadCsv(lessonsResponse.data, "learning_swing_lessons.csv");
};
